import { Point, Element, ElementType } from '../types/drawing';
import { IdGenerator } from '../domain/ports/id';
import { Clock } from '../domain/ports/clock';
import { defaultId } from '../runtime/id';
import { systemClock } from '../runtime/clock';
import { validateElement } from '../domain/validation/element';
import { devLog } from '../runtime/logger';

export const getDistance = (p1: Point, p2: Point): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

export const getMidpoint = (p1: Point, p2: Point): Point => {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
};

export const getBoundingBox = (points: Point[]): { minX: number; minY: number; maxX: number; maxY: number } => {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }

  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return { minX, minY, maxX, maxY };
};

export const isPointInElement = (point: Point, element: Element): boolean => {
  const bbox = getBoundingBox(element.points);
  const tolerance = element.strokeWidth / 2;

  return (
    point.x >= bbox.minX - tolerance &&
    point.x <= bbox.maxX + tolerance &&
    point.y >= bbox.minY - tolerance &&
    point.y <= bbox.maxY + tolerance
  );
};

export const createElement = (
  type: ElementType,
  points: Point[],
  config: {
    strokeWidth: number;
    strokeColor: string;
    fillColor: string;
    fillOpacity: number;
    layerId: string;
  },
  ports?: {
    idGen?: IdGenerator;
    clock?: Clock;
  }
): Element => {
  const idGen = ports?.idGen ?? defaultId;
  const clock = ports?.clock ?? systemClock;
  const element: Element = {
    id: idGen.makeId(),
    type,
    points: [...points],
    strokeWidth: config.strokeWidth,
    strokeColor: config.strokeColor,
    fillColor: config.fillColor,
    fillOpacity: config.fillOpacity,
    layerId: config.layerId,
    createdAt: clock.now(),
    updatedAt: clock.now(),
  };
  const res = validateElement(element);
  if (!res.ok) {
    devLog('validateElement warnings on createElement:', res.error);
  }
  return element;
};

export const updateElement = (
  element: Element,
  updates: Partial<Element>,
  ports?: {
    clock?: Clock;
  }
): Element => {
  const clock = ports?.clock ?? systemClock;
  // Ignore immutable properties from updates to preserve identity and creation time
  const rest: Partial<Element> = { ...updates };
  if ('id' in rest) delete rest.id;
  if ('createdAt' in rest) delete rest.createdAt;
  if ('updatedAt' in rest) delete rest.updatedAt;
  const next: Element = {
    ...element,
    ...rest,
    updatedAt: clock.now(),
  };
  return next;
};
